import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Send } from "lucide-react";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-none border-0 border-b border-white/20 bg-transparent px-0 py-3 font-sans text-sm text-[#F4F4F0] placeholder:text-[#57534E] focus:border-[#E63946] focus:outline-none transition-colors duration-300";

const TOPICS = [
  { value: "reader", label: "Reader question" },
  { value: "media", label: "Media & press" },
  { value: "wholesale", label: "Wholesale & bookstores" },
  { value: "rights", label: "Rights & translation" },
];

const labelCls = "mb-1 block font-sans text-[10px] font-bold uppercase tracking-[0.3em] text-[#A8A29E]";

export const ContactSection = () => {
  const [newsEmail, setNewsEmail] = useState("");
  const [newsLoading, setNewsLoading] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", topic: "reader", message: "" });
  const [sending, setSending] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (newsLoading) return;
    setNewsLoading(true);
    try {
      const res = await fetch(`${API}/newsletter`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: newsEmail }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail ? "Please enter a valid email." : "Failed");
      toast.success(data.message || "Welcome aboard.");
      setNewsEmail("");
    } catch (err) {
      toast.error(err.message || "Could not subscribe. Try again.");
    } finally {
      setNewsLoading(false);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error("Please check your details and try again.");
      toast.success(data.message || "Message received.");
      setForm({ name: "", email: "", topic: "reader", message: "" });
    } catch (err) {
      toast.error(err.message || "Could not send. Try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative border-t border-white/10 py-32">
      <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-20 px-6 md:px-12 lg:grid-cols-2">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-15%" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        >
          <div className="mb-6 flex items-center gap-4">
            <span className="h-px w-10 bg-[#E63946]" />
            <p className="font-sans text-[10px] font-bold uppercase tracking-[0.4em] text-[#A8A29E]">
              The Dispatch
            </p>
          </div>
          <h2 className="font-serif text-4xl font-bold leading-[1.02] tracking-tight md:text-6xl">
            Join the
            <br />
            <span className="italic font-medium text-[#A8A29E]">journey.</span>
          </h2>
          <p className="mt-6 max-w-md font-sans text-base font-light leading-relaxed text-[#A8A29E]">
            New releases, readings and the occasional haiku — straight to your
            inbox. No noise, ever.
          </p>
          <form onSubmit={subscribe} className="mt-10 flex max-w-md items-end gap-4" data-testid="newsletter-form">
            <div className="flex-1">
              <label htmlFor="newsletter-email" className={labelCls}>
                Email address
              </label>
              <input
                id="newsletter-email"
                type="email"
                required
                value={newsEmail}
                onChange={(e) => setNewsEmail(e.target.value)}
                placeholder="you@example.com"
                data-testid="newsletter-email-input"
                className={inputCls}
              />
            </div>
            <button
              type="submit"
              disabled={newsLoading}
              data-testid="newsletter-subscribe-button"
              className="group flex items-center gap-2 bg-[#E63946] px-6 py-3.5 font-sans text-[11px] font-bold uppercase tracking-[0.25em] text-white transition-colors duration-300 hover:bg-[#F4F4F0] hover:text-[#0C0A09] disabled:opacity-60"
            >
              {newsLoading ? "Joining…" : "Subscribe"}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </form>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-15%" }}
          transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
        >
          <div className="mb-6 flex items-center gap-4">
            <span className="h-px w-10 bg-[#E63946]" />
            <p className="font-sans text-[10px] font-bold uppercase tracking-[0.4em] text-[#A8A29E]">
              Write to us
            </p>
          </div>
          <h3 className="font-serif text-3xl font-bold tracking-tight md:text-4xl">
            Media, wholesale & rights
          </h3>
          <form onSubmit={sendMessage} className="mt-10 space-y-8" data-testid="contact-form">
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2">
              <div>
                <label htmlFor="contact-name" className={labelCls}>Name</label>
                <input
                  id="contact-name"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Your name"
                  data-testid="contact-name-input"
                  className={inputCls}
                />
              </div>
              <div>
                <label htmlFor="contact-email" className={labelCls}>Email</label>
                <input
                  id="contact-email"
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="you@example.com"
                  data-testid="contact-email-input"
                  className={inputCls}
                />
              </div>
            </div>
            <div>
              <label className={labelCls}>Topic</label>
              <Select value={form.topic} onValueChange={(v) => setForm({ ...form, topic: v })}>
                <SelectTrigger data-testid="contact-topic-select" className="rounded-none border-0 border-b border-white/20 bg-transparent px-0 font-sans text-sm text-[#F4F4F0] focus:ring-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-none border-white/10 bg-[#141210] text-[#F4F4F0]">
                  {TOPICS.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label htmlFor="contact-message" className={labelCls}>Message</label>
              <textarea
                id="contact-message"
                required
                rows={4}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="Tell us what you have in mind…"
                data-testid="contact-message-input"
                className={`${inputCls} resize-none`}
              />
            </div>
            <button
              type="submit"
              disabled={sending}
              data-testid="contact-send-button"
              className="group flex items-center gap-3 border border-[#E63946] px-7 py-4 font-sans text-[11px] font-bold uppercase tracking-[0.25em] text-[#E63946] transition-colors duration-300 hover:bg-[#E63946] hover:text-white disabled:opacity-60"
            >
              {sending ? "Sending…" : "Send message"}
              <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-0.5" />
            </button>
          </form>
        </motion.div>
      </div>
    </section>
  );
};
